import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { DemoDockNav } from './DemoDockNav';
import type { useDemoDockCredits } from './Credits/credits';
import type { DemoDockAuthState } from './auth';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

type ChatSidebarProps = {
  auth: DemoDockAuthState;
  credits: ReturnType<typeof useDemoDockCredits>;
  messages: ChatMessage[];
  onSend: (text: string) => void;
};

export function ChatSidebar({ auth, credits, messages, onSend }: ChatSidebarProps) {
  const [draft, setDraft] = useState('');
  const signInDisabled = !auth.loginScreenUrl && auth.user?.provider === 'local';

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  }

  return (
    <div className="demo-dock-chat">
      <div className="demo-dock-chat-header">
        <h1>Demo Dock</h1>
        <DemoDockNav />
        <div className="demo-dock-account">
          <div>
            <p className="demo-dock-account-label">Account</p>
            <p className="demo-dock-account-user">
              {auth.status === 'loading'
                ? 'Checking sign-in...'
                : auth.user?.name ?? 'Not signed in'}
            </p>
          </div>
          {auth.status === 'authenticated' ? (
            auth.logoutUrl ? (
              <a href={auth.logoutUrl}>Sign out</a>
            ) : (
              <span>Local</span>
            )
          ) : (
            <a aria-disabled={signInDisabled} href={auth.loginScreenUrl || undefined}>
              Sign in
            </a>
          )}
        </div>
        {auth.status === 'error' ? <p className="demo-dock-auth-note">{auth.error}</p> : null}
        <p>
          Try: <span>Change title to Your headline</span>
        </p>
        <div className="demo-dock-credit-summary">
          <div>
            <p className="demo-dock-credit-label">{credits.sku.name} plan</p>
            <p className="demo-dock-credit-count">
              {credits.remainingCredits} / {credits.totalCredits} credits left
            </p>
          </div>
          <Link to="/projects/demodock/skus">Upgrade</Link>
        </div>
      </div>

      <div className="demo-dock-messages" aria-live="polite">
        {messages.length === 0 ? (
          <p className="demo-dock-empty">Send a message to drive the demo.</p>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`demo-dock-message demo-dock-message-${message.role}`}
            >
              {message.text}
            </div>
          ))
        )}
      </div>

      <form onSubmit={handleSubmit} className="demo-dock-composer">
        <label htmlFor="demo-dock-chat-input">Message</label>
        <textarea
          id="demo-dock-chat-input"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a command or question..."
        />
        <button type="submit">Send</button>
      </form>
    </div>
  );
}
